import { ListAttribution, WishlistsActionOrigin, WishlistsActionsOriginType } from '@spa/lib/tracking/trackWishlistsActions';
import PopoverComponent, { PopoverPositions } from '@spa/components/popover/popover.component';
import PopupComponent, { PopupType } from '@spa/components/popup/popup.component';
import React, { ReactElement, memo, useCallback, useEffect, useRef, useState } from 'react';
import { StateWishlist, StateWishlistItem } from '@spa/lib/store/wishlists/WishlistsState';
import { addProductToMyWishlistRequested, createMyWishlistRequested, deleteProductFromMyWishlistRequested } from '@spa/lib/store/wishlists/wishlistsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { DeleteProductFromMyWishlistInput } from '@spa/generated/graphql/graphql';
import ImageComponent from '@spa/components/image-component/image.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { SessionsData } from '@spa/lib/auth/sessionTypes';
import { User } from 'next-auth';
import WishlistMenuComponent from '@spa/components/wishlist/wishlist-menu/wishlist-menu.component';
import { WishlistTrackingProduct } from '@spa/lib/tracking/productsToAnalyticsItemConverter';
import classNames from 'clsx';
import { createPortal } from 'react-dom';
import findDeleteFromWishlistParams from '@spa/lib/wishlist/findDeleteFromWishlistParams';
import generateActionId from '@spa/lib/generateActionId';
import selectDefaultWishlist from '@spa/lib/store/wishlists/selectors/selectDefaultWishlist';
import selectFailedAddProductToMyWishlistActionIds from '@spa/lib/store/wishlists/selectors/selectFailedAddProductToMyWishlistActionIds';
import selectFailedCreateMyWishlistActionIds from '@spa/lib/store/wishlists/selectors/selectFailedCreateMyWishlistActionIds';
import selectFailedDeleteProductFromMyWishlistActionIds from '@spa/lib/store/wishlists/selectors/selectFailedDeleteProductFromMyWishlistActionIds';
import selectSessionSelectedWishlist from '@spa/lib/store/session/selectors/selectSessionSelectedWishlist';
import selectSucceededAddProductToMyWishlistActionIds from '@spa/lib/store/wishlists/selectors/selectSucceededAddProductToMyWishlistActionIds';
import selectSucceededCreateMyWishlistActionIds from '@spa/lib/store/wishlists/selectors/selectSucceededCreateMyWishlistActionIds';
import selectSucceededDeleteProductFromMyWishlistActionIds from '@spa/lib/store/wishlists/selectors/selectSucceededDeleteProductFromMyWishlistActionIds';
import selectWishlists from '@spa/lib/store/wishlists/selectors/selectWishlists';
import styles from './wishlist.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useNextAuthOrSession from '@spa/lib/react/hooks/useNextAuthOrSession';
export interface Props {
  readonly actionOrigin: WishlistsActionsOriginType;
  readonly afterAuthCallbackUrl: string;
  readonly listAttribution?: ListAttribution;
  readonly product: WishlistTrackingProduct;
  readonly quantity: number;
  readonly useColor?: boolean;
}
interface FindDeleteFromWistParamsResult {
  readonly input: DeleteProductFromMyWishlistInput;
  readonly isDefault: boolean;
}
function WishlistComponent({
  actionOrigin,
  afterAuthCallbackUrl,
  listAttribution,
  product,
  quantity,
  useColor = true
}: Props): ReactElement {
  const productCode: string = product.code;
  const wishlistContext: WishlistsActionOrigin = actionOrigin.originType;
  const {
    formatMessage
  }: NamespacedIntl = useNamespacedIntl('wishlist');
  const addToWishlistTranslation: string = formatMessage('addToWishlist', {
    productCode
  });
  const pathname = usePathname();
  const queryParams = useSearchParams();
  const router = useRouter();
  const addProductToWishlistParam: string | null = queryParams.get('addProductToWishlist');
  const shouldAddProductToWishlist: boolean = addProductToWishlistParam === productCode;
  const dispatch = useDispatch();
  const {
    authSession
  }: SessionsData = useNextAuthOrSession();
  const user: User | null = authSession?.user ?? null;
  const isAnonymousOrGuestUser: boolean = user === null;
  const addParam = `addProductToWishlist=${productCode}`;
  const separator: string = afterAuthCallbackUrl.includes('?') ? '&' : '?';
  const callbackUrl: string = `${afterAuthCallbackUrl}${separator}${addParam}`;
  const hrefLink: string = `/auth/login-register/?callbackUrl=${encodeURIComponent(callbackUrl)}`;
  const [showWishlistMenuPopover, setShowWishlistMenuPopover] = useState(false);
  const [showWishlistMenuPopup, setShowWishlistMenuPopup] = useState(false);
  const [showAddWishlistPopup, setShowAddWishlistPopup] = useState(false);
  const [shouldListBeOpenedAfterAction, setShouldListBeOpenedAfterAction] = useState<boolean>(false);
  const [popoverWasClicked, setPopoverWasClicked] = useState<boolean>(false);
  const wishlists: ReadonlyArray<StateWishlist> = useSelector(selectWishlists);
  const defaultWishlist: StateWishlist | undefined = useSelector(selectDefaultWishlist);
  const selectedWishlist: string | null = useSelector(selectSessionSelectedWishlist);
  const [lastCreateNewWishlistActionId, setLastCreateNewWishlistActionId] = useState<number | null>(null);
  const succeededCreateWishlistActionIds: ReadonlyArray<number> = useSelector(selectSucceededCreateMyWishlistActionIds);
  const failedCreateWishlistActionIds: ReadonlyArray<number> = useSelector(selectFailedCreateMyWishlistActionIds);
  const [lastAddProductToMyWishlistActionId, setLastAddProductToMyWishlistActionId] = useState<number | null>(null);
  const succeededAddProductToWishlistActionIds: ReadonlyArray<number> = useSelector(selectSucceededAddProductToMyWishlistActionIds);
  const failedAddProductToWishlistActionIds: ReadonlyArray<number> = useSelector(selectFailedAddProductToMyWishlistActionIds);
  const [lastDeleteProductFromMyWishlistActionId, setLastDeleteProductFromMyWishlistActionId] = useState<number | null>(null);
  const succeededDeleteProductFromWishlistActionIds: ReadonlyArray<number> = useSelector(selectSucceededDeleteProductFromMyWishlistActionIds);
  const failedDeleteProductFromWishlistActionIds: ReadonlyArray<number> = useSelector(selectFailedDeleteProductFromMyWishlistActionIds);
  const isCreatingNewWishlist: boolean = lastCreateNewWishlistActionId !== null && !succeededCreateWishlistActionIds.includes(lastCreateNewWishlistActionId) && !failedCreateWishlistActionIds.includes(lastCreateNewWishlistActionId);
  const isAddingProductToWishlist: boolean = lastAddProductToMyWishlistActionId !== null && !succeededAddProductToWishlistActionIds.includes(lastAddProductToMyWishlistActionId) && !failedAddProductToWishlistActionIds.includes(lastAddProductToMyWishlistActionId);
  const isDeletingProductFromWishlist: boolean = lastDeleteProductFromMyWishlistActionId !== null && !succeededDeleteProductFromWishlistActionIds.includes(lastDeleteProductFromMyWishlistActionId) && !failedDeleteProductFromWishlistActionIds.includes(lastDeleteProductFromMyWishlistActionId);
  const findProductWishlists = (): Map<string, StateWishlist> => {
    const productWishlists = new Map<string, StateWishlist>();
    wishlists.forEach((wishlist: StateWishlist) => {
      const {
        id,
        items
      }: StateWishlist = wishlist;
      if (items.find(({
        sku
      }: StateWishlistItem) => sku === productCode) !== undefined) {
        productWishlists.set(id, wishlist);
      }
    });
    return productWishlists;
  };
  const productWishlists_0 = findProductWishlists();
  const isProductInWishlist = (wishlistId: string): boolean => productWishlists_0.has(wishlistId);
  const isProductWishlisted: boolean = findProductWishlists().size > 0 || isCreatingNewWishlist || isAddingProductToWishlist || shouldListBeOpenedAfterAction;
  const createDefaultWishlist = (): void => {
    const actionId: number = generateActionId();
    setLastCreateNewWishlistActionId(actionId);
    dispatch(createMyWishlistRequested({
      actionId,
      input: {
        description: null,
        isDefault: true,
        name: formatMessage('defaultWishlist.name')
      },
      isDefault: true,
      listAttribution,
      origin: actionOrigin,
      products: [product]
    }));
  };
  const addProductToWishlist = (wishlistId_0: string, isDefault: boolean): void => {
    const actionId_0: number = generateActionId();
    setLastAddProductToMyWishlistActionId(actionId_0);
    if (actionOrigin) {
      const params: FindDeleteFromWistParamsResult | undefined = findDeleteFromWishlistParams(wishlists, productCode);
      dispatch(addProductToMyWishlistRequested({
        actionId: actionId_0,
        input: {
          itemSku: productCode,
          quantity,
          wishlistId: wishlistId_0
        },
        isDefault,
        listAttribution,
        product,
        deletedWishlist: params !== undefined ? params.input : undefined
      }));
    }
  };
  const removeFromAnyWishlist = (): void => {
    const params_0: FindDeleteFromWistParamsResult | undefined = findDeleteFromWishlistParams(wishlists, productCode);
    if (params_0 !== undefined) {
      const {
        input,
        isDefault: isDefault_0
      } = params_0;
      const deleteProductFromMyWishlistActionId: number = generateActionId();
      setLastDeleteProductFromMyWishlistActionId(deleteProductFromMyWishlistActionId);
      dispatch(deleteProductFromMyWishlistRequested({
        actionId: deleteProductFromMyWishlistActionId,
        input,
        isDefault: isDefault_0,
        product
      }));
    }
  };
  const removeAddProductToWishlistParam = (): void => {
    const newQuery = new URLSearchParams(queryParams);
    newQuery.delete('addProductToWishlist');
    const fullPath = pathname + '?' + newQuery.toString();
    router.replace(fullPath);
  };
  if (shouldAddProductToWishlist && !isProductWishlisted) {
    removeAddProductToWishlistParam();
    if (defaultWishlist) {
      addProductToWishlist(defaultWishlist.id, true);
    } else {
      createDefaultWishlist();
    }
  }
  let wishlistIconName: string = 'favorite';
  let wishlistIconAlt: string;
  if (isProductWishlisted || showAddWishlistPopup || showWishlistMenuPopover) {
    wishlistIconName = `${wishlistIconName}-solid`;
    wishlistIconAlt = formatMessage('removeFromWishlist', {
      productCode
    });
  } else {
    wishlistIconAlt = addToWishlistTranslation;
  }
  if (!useColor) {
    wishlistIconName = `${wishlistIconName}-grey`;
  }
  const getWishlistIconSize = (): number => {
    let result: number;
    switch (wishlistContext) {
      case WishlistsActionOrigin.CART:
        result = 24;
        break;
      case WishlistsActionOrigin.PDP:
      case WishlistsActionOrigin.PLP:
      case WishlistsActionOrigin.PRODUCT_CAROUSEL:
      case WishlistsActionOrigin.NAVIGATION_FLYOUT:
        result = 32;
        break;
    }
    return result;
  };
  const wishlistIconSize: number = getWishlistIconSize();
  const wishlistClickableElement: ReactElement = <>
      <ImageComponent src={`/assets/icons/${wishlistIconName}.svg`} alt={wishlistIconAlt} width={wishlistIconSize} height={wishlistIconSize} />
      {wishlistContext === WishlistsActionOrigin.CART && <span>{formatMessage('wishlist')}</span>}
    </>;
  if (shouldListBeOpenedAfterAction && (lastCreateNewWishlistActionId !== null && succeededAddProductToWishlistActionIds.includes(lastCreateNewWishlistActionId) || lastAddProductToMyWishlistActionId !== null && succeededAddProductToWishlistActionIds.includes(lastAddProductToMyWishlistActionId))) {
    setShouldListBeOpenedAfterAction(false);
    if (popoverWasClicked) {
      setShowWishlistMenuPopover(true);
      setPopoverWasClicked(false);
    } else {
      setShowWishlistMenuPopup(true);
    }
  }
  const shouldButtonBeDisabled: boolean = isCreatingNewWishlist || isAddingProductToWishlist || isDeletingProductFromWishlist;
  const closeWishlistWithDelay = (): void => {
    setTimeout(() => {
      setShowWishlistMenuPopover(false);
      setShowWishlistMenuPopup(false);
      focusTriggerButton();
    }, 800);
  };
  const wishlistMenu: ReactElement = <WishlistMenuComponent closeWishlistWithDelay={closeWishlistWithDelay} deleteWishlistPayload={findDeleteFromWishlistParams(wishlists, productCode)?.input} disabled={shouldButtonBeDisabled} showAddWishlistPopup={showAddWishlistPopup} setShowAddWishlistPopup={setShowAddWishlistPopup} origin={actionOrigin} product={product} selectedIdx={wishlists.findIndex(({
    id: id_0
  }: StateWishlist) => isProductInWishlist(id_0))} onListItemClick={(itemId: string, isDefault_1: boolean) => {
    !isProductInWishlist(itemId) && addProductToWishlist(itemId, isDefault_1);
    closeWishlistWithDelay();
  }} />;
  const isDefaultWishlistSelected = (selectedWishlistId: string): boolean => defaultWishlist?.id === selectedWishlistId;
  const getSelectedWishlistId = (defaultWishlistId: string): string => {
    const targetSelectedWishlist: StateWishlist | undefined = wishlists.find(wishlist_0 => wishlist_0.id === selectedWishlist);
    return targetSelectedWishlist?.id ?? defaultWishlistId;
  };
  const handlePopupClick = (): void => {
    if (isAnonymousOrGuestUser) {
      router.push(hrefLink);
    } else {
      if (!isProductWishlisted && !defaultWishlist) {
        setShouldListBeOpenedAfterAction(true);
        createDefaultWishlist();
      } else if (!isProductWishlisted && defaultWishlist) {
        setShouldListBeOpenedAfterAction(true);
        const selectedWishlistId_0: string = getSelectedWishlistId(defaultWishlist.id);
        const isDefaultWishlist: boolean = isDefaultWishlistSelected(selectedWishlistId_0);
        addProductToWishlist(selectedWishlistId_0, isDefaultWishlist);
      } else {
        removeFromAnyWishlist();
        setShowWishlistMenuPopup(false);
      }
    }
  };
  const handlePopoverClick = (): void => {
    if (isAnonymousOrGuestUser) {
      router.push(hrefLink);
    } else {
      if (!isProductWishlisted && !defaultWishlist) {
        setPopoverWasClicked(true);
        setShouldListBeOpenedAfterAction(true);
        createDefaultWishlist();
      } else if (!isProductWishlisted && defaultWishlist) {
        setPopoverWasClicked(true);
        setShouldListBeOpenedAfterAction(true);
        const selectedWishlistId_1: string = getSelectedWishlistId(defaultWishlist.id);
        const isDefaultWishlist_0: boolean = isDefaultWishlistSelected(selectedWishlistId_1);
        addProductToWishlist(selectedWishlistId_1, isDefaultWishlist_0);
      } else {
        removeFromAnyWishlist();
        setShowWishlistMenuPopover(false);
        focusTriggerButton();
      }
    }
  };
  const wishlistClassnames: string = classNames(styles['wishlist'], {
    [styles['wishlist--cart']]: wishlistContext === WishlistsActionOrigin.CART,
    [styles['wishlist--pdp']]: wishlistContext === WishlistsActionOrigin.PDP,
    [styles['wishlist--plp']]: wishlistContext === WishlistsActionOrigin.PLP,
    [styles['wishlist--navigation-flyout']]: wishlistContext === WishlistsActionOrigin.NAVIGATION_FLYOUT,
    [styles['wishlist--carousel']]: wishlistContext === WishlistsActionOrigin.PRODUCT_CAROUSEL
  });
  const triggerClassnames: string = classNames(styles['wishlist__trigger'], {
    [styles['wishlist__trigger--loading']]: shouldButtonBeDisabled
  });
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const triggerWrapperRef = useRef<HTMLDivElement>(null);
  const focusTriggerButton = useCallback((): void => {
    setTimeout(() => {
      triggerWrapperRef.current?.querySelector('button')?.focus();
      triggerButtonRef.current?.focus();
    }, 250);
  }, []);
  const handleKeyDown = useCallback((e: KeyboardEvent): void => {
    if (e.key === 'Escape' && (showWishlistMenuPopover || showAddWishlistPopup)) {
      setShowWishlistMenuPopover(false);
      focusTriggerButton();
    }
  }, [showWishlistMenuPopover, showAddWishlistPopup, setShowWishlistMenuPopover, focusTriggerButton]);
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showWishlistMenuPopover, showAddWishlistPopup, handleKeyDown]);
  return <div className={wishlistClassnames}>
      <div className={styles['wishlist__desktop-wrapper']}>
        <PopoverComponent contents={wishlistMenu} positions={[PopoverPositions.BOTTOM]} isPopoverOpen={showWishlistMenuPopover} onClosed={() => {
        if (!showAddWishlistPopup) {
          setShowWishlistMenuPopover(false);
          focusTriggerButton();
        }
      }} isInFlyout={actionOrigin.originType === WishlistsActionOrigin.NAVIGATION_FLYOUT}>
          <div ref={triggerWrapperRef}>
            <button ref={triggerButtonRef} className={triggerClassnames} onClick={handlePopoverClick} disabled={shouldButtonBeDisabled} role={isAnonymousOrGuestUser ? 'link' : 'button'}>
              {wishlistClickableElement}
            </button>
          </div>
        </PopoverComponent>
      </div>
      <div className={styles['wishlist__mobile-wrapper']}>
        <button onClick={handlePopupClick} className={triggerClassnames} disabled={shouldButtonBeDisabled} role={isAnonymousOrGuestUser ? 'link' : 'button'}>
          {wishlistClickableElement}
        </button>
        {!showWishlistMenuPopover && showWishlistMenuPopup && createPortal(<PopupComponent isOpen={showWishlistMenuPopup} onClosed={(): void => setShowWishlistMenuPopup(false)} popupType={PopupType.DEFAULT}>
              {wishlistMenu}
            </PopupComponent>, document.body)}
      </div>
    </div>;
}
export default memo(WishlistComponent);